import React, { useContext, useEffect, useState } from 'react';

import { Typography } from '@mui/material';
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import { db } from '../../../api/firebase/config';
import { companiesRef } from '../../../api/firebase/firestore/company';
import { issuesRef } from '../../../api/firebase/firestore/issue';
import { mediasRef } from '../../../api/firebase/firestore/media';
import { getUserInfo } from '../../../api/firebase/firestore/user';
import { mypageHeader, mypageHeaderTitle } from '../../../constants/Location';
import { screenType, status } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import CustomLoading from '../../uiParts/CustomLoading';
import NothingGivenList from '../../uiParts/NothingGivenList';
import PublisherIssueCard from '../../uiParts/PublisherIssueCard';
import Sidebar from '../../uiParts/Sidebar';

import type { Issue } from '../../../api/firebase/firestore/issue/issueType';
import type {
  DocumentData,
  DocumentSnapshot,
  QueryDocumentSnapshot
} from 'firebase/firestore';

const styles = {
  card: {
    '@media screen and (max-width:470px)': {
      mt: '12px'
    },
    mt: '24px'
  },
  list: {
    '@media screen and (max-width:470px)': {
      margin: '0 16px'
    },
    margin: '0 24px'
  },
  mypageHeader,
  mypageHeaderTitle,
  sideBar: {
    '@media screen and (max-width:960px)': {
      display: 'block'
    },
    display: 'flex'
  }
};

const PublisherMypageFavoriteList: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    document.title = t('title.publisher.favoriteList');
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState(true);
  const [issueList, setIssueList] = useState<Issue[]>([]);
  const [showList, setShowList] = useState<boolean>(false);

  const appendQuestions = async (
    snapshot: Array<DocumentSnapshot<DocumentData>>
  ): Promise<void> => {
    const companies = query(
      companiesRef,
      where('status', '==', status.approved)
    );

    const companiesSnaps = await getDocs(companies);

    let activeCompanyIds: string[] = [];
    companiesSnaps.forEach((companySnap) => {
      if (companySnap.data().status === status.approved) {
        activeCompanyIds.push(companySnap.data().companyId as string);
      }
    });

    activeCompanyIds = activeCompanyIds.filter((item) => item !== undefined);

    let gotIssues: Issue[] = snapshot.map((document) => {
      const issue = document.data() as Issue;
      return issue;
    });

    gotIssues = gotIssues.filter((issue) =>
      activeCompanyIds.includes(`${issue.companyId}`)
    );

    setIssueList(issueList.concat(gotIssues));
  };

  const fetchFavoriteIssues = async (
    uid: string,
    lastIssue: Issue | null | undefined
  ): Promise<QueryDocumentSnapshot[]> => {
    let favoriteIssueQuery;

    if (lastIssue === null || lastIssue === undefined) {
      favoriteIssueQuery = query(
        collectionGroup(db, 'favoriteIssue'),
        where('uid', '==', uid),
        orderBy('created', 'desc'),
        limit(50)
      );
    } else {
      favoriteIssueQuery = query(
        collectionGroup(db, 'favoriteIssue'),
        where('uid', '==', uid),
        orderBy('created', 'desc'),
        startAfter(lastIssue?.created),
        limit(50)
      );
    }

    const favoriteIssuesSnap = await getDocs(favoriteIssueQuery);

    const IssuePromises = favoriteIssuesSnap.docs.map(
      async (favoriteIssueSnap) => {
        const favoriteIssueData = favoriteIssueSnap.data();
        const issueId = favoriteIssueData?.issueId as string;
        if (!issueId) {
          return null;
        }
        const issueRef = doc(issuesRef, issueId);
        const issueSnap = await getDoc(issueRef);
        const issueData = issueSnap.data();
        if (!issueSnap.exists() || issueData?.status !== status.approved) {
          return null;
        }

        return issueSnap;
      }
    );

    const issueSnaps: Array<QueryDocumentSnapshot | null> = await Promise.all(
      IssuePromises
    );
    const issueQueryDocumentSnapshot = issueSnaps.filter(
      (issueSnap) => issueSnap !== null
    ) as QueryDocumentSnapshot[];

    return issueQueryDocumentSnapshot;
  };

  const loadMore = async (): Promise<void> => {
    if (currentUser) {
      let lastIssue;
      if (issueList.length > 0) {
        lastIssue = issueList[issueList.length - 1];
      }

      let datas;
      if (issueList.length) {
        lastIssue = issueList[issueList.length - 1];
        datas = await fetchFavoriteIssues(currentUser.uid, lastIssue);
      } else {
        datas = await fetchFavoriteIssues(currentUser.uid, null);
      }

      if (datas === null) {
        setHasMore(false);
        return;
      }
      await appendQuestions(datas);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await loadMore();
      } catch (e) {
        throwError(e);
      } finally {
        setShowList(true);
        setLoading(false);
      }
    })();
  }, [currentUser, issueList.length]);

  // メディアリストの件数取得
  const [cantInquiry, setCantInquiry] = useState<string | undefined>(undefined);

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const userData = await getUserInfo(currentUser.uid);
        const comId = userData?.companyId as string;
        const comRef = doc(companiesRef, comId);
        const comSnap = await getDoc(comRef);
        const comData = comSnap.data();
        if (comData?.status !== status.approved) {
          setCantInquiry(t('issueFavoLike.unApprovedCompany'));
        }
        const q = query(mediasRef, where('companyId', '==', comId));
        const { empty, docs } = await getDocs(q);
        if (empty) {
          setCantInquiry(t('issueFavoLike.noMedia'));
        } else {
          const approvedDocsCount = docs.filter(
            (document) => document.data().status === status.approved
          ).length;
          if (approvedDocsCount === 0) {
            setCantInquiry(t('issueFavoLike.unApprovedMedia'));
          }
        }
      }
    })();
  }, []);

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.sideBar}>
        <Sidebar screenType={screenType.publisher} />
        <Typography
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'static',
            width: '100%'
          }}
        >
          <Typography sx={styles.mypageHeader} component="div">
            <Typography sx={styles.mypageHeaderTitle}>
              {t('issueFavoLike.favoriteList')}
            </Typography>
          </Typography>
          <Typography component="div">
            {showList && (
              <Typography component="div" sx={{ mb: '40px' }}>
                {issueList.length !== 0 ? (
                  <Typography sx={styles.list} component="div">
                    <InfiniteScroll
                      loadMore={() => {
                        void (async () => {
                          await loadMore();
                        })();
                      }}
                      hasMore={hasMore}
                      loader={<div className="loader" key={0}></div>}
                    >
                      {issueList.map((value, i) => (
                        <Typography key={i} sx={styles.card} component="div">
                          <PublisherIssueCard
                            issue={value}
                            cantInquiry={cantInquiry}
                          />
                        </Typography>
                      ))}
                    </InfiniteScroll>
                  </Typography>
                ) : (
                  <NothingGivenList
                    title={t('issueFavoLike.nothingFavoriteList')}
                    detail={t('issueFavoLike.registerFavoriteList')}
                    type="issueFavorite"
                  />
                )}
              </Typography>
            )}
          </Typography>
        </Typography>
      </Typography>
    </>
  );
};

export default PublisherMypageFavoriteList;
